import type { Router } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { isSignupEnabled } from '@/composables/useSignupEnabled'

/**
 * Guards de navegación del portal.
 *
 * Se reemplaza el guard de la plantilla (que leía cookies + CASL) por el
 * modelo que ya usábamos:
 *   - `requiresAuth` → sin sesión, va a login conservando el destino en `next`.
 *   - `publicOnly`   → con sesión, no tiene sentido ver login/registro.
 *   - `ownerOnly`    → solo el owner; el backend valida lo mismo.
 *   - `centerConfig` → configurar el centro (ficha, ofertas, temporadas,
 *     cierres): todos menos `staff`. Sin esto, una pantalla de edición se abre
 *     igual escribiendo la URL y recién falla al guardar, con un 403 que no
 *     explica nada después de haber llenado un formulario entero.
 *   - `signupOnly`   → pasos del alta self-service; con el alta cerrada
 *     rebotan a login.
 *   - `comingSoon`   → la pantalla existe pero todavía no se ofrece (§60). El
 *     menú y Settings ya la muestran apagada; esto cierra la puerta de la URL,
 *     que es la que quedaba abierta.
 *
 * `auth.hydrate()` levanta la sesión cacheada de localStorage en cada
 * navegación: el token no es reactivo por sí solo (ver CLAUDE.md §3.5).
 */
export const setupGuards = (router: Router) => {
  router.beforeEach(async to => {
    const auth = useAuthStore()

    auth.hydrate()

    if (to.meta.requiresAuth && !auth.isAuthenticated)
      return { name: 'login', query: { next: to.fullPath } }

    if (to.meta.publicOnly && auth.isAuthenticated)
      return { name: 'dashboard' }

    if (to.meta.ownerOnly && auth.providerUser?.role !== 'owner')
      return { name: 'dive-centers' }

    // Se rebota al Overview del centro y no a una pantalla de error: staff
    // tiene acceso legítimo al centro, solo que no a configurarlo.
    if (to.meta.centerConfig && auth.providerUser?.role === 'staff') {
      const id = to.params.id

      return id
        ? { name: 'dive-centers-id', params: { id } }
        : { name: 'dashboard' }
    }

    /*
     * El alta pública está cerrada salvo que el flag la reabra. Se consulta
     * acá y no en cada página para que las tres pantallas del alta compartan
     * la decisión; `isSignupEnabled` cachea la respuesta, así que solo la
     * primera navegación paga la ida a la API.
     *
     * Va último a propósito: los chequeos de sesión son sincrónicos y no
     * deberían esperar por una llamada de red que casi nunca hace falta.
     */
    /*
     * Pantalla terminada pero todavía no ofrecida (§60). Rebota a Settings y no
     * a una pantalla de error: no está rota ni prohibida, simplemente no es su
     * momento, y el lugar donde la va a encontrar cuando lo sea es ese.
     *
     * Quitar el `meta` es todo lo que hace falta para encenderla.
     */
    if (to.meta.comingSoon)
      return { name: 'settings' }

    if (to.meta.signupOnly && !(await isSignupEnabled()))
      return { name: 'login' }

    return true
  })
}
